<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useDisplay } from 'vuetify';
import { sendNps } from '@/api/NpsApi';
import type { Nps } from '@/models/Nps';

import gettext from '@/i18n/gettext';

const { smAndUp } = useDisplay();
const { $gettext } = gettext;

const colors = ['red', 'orange', 'grey', 'cyan', 'green'];
const labels = computed(() => [
  $gettext('BAD<br/>🙁'),
  $gettext('SO SO<br/>😑'),
  $gettext('OKAY<br/>🙂'),
  $gettext('GOOD<br/>🦾'),
  $gettext('GREAT<br/>🚀'),
]);

const showOverlay = ref(false);
const nps = ref<Nps>({
  score: 0,
  comment: '',
});

function close() {
  showOverlay.value = false;
}

async function submitFeedback() {
  await sendNps(nps.value);
  close();
}

async function dontAskAgain() {
  await sendNps({});
  close();
}

onMounted(async () => {
  showOverlay.value = false;
  // TODO: re-enable NPS pop up
  // showOverlay.value = await npsAvailable();
});
</script>
<template>
  <v-card
    v-if="showOverlay"
    class="rate-experience"
    :class="smAndUp ? '' : 'rate-experience--mobile'"
  >
    <v-card-title class="rate-experience__title">
      {{ $gettext('Rate your RobCo Experience') }}
    </v-card-title>
    <v-card-subtitle>
      {{ $gettext('Would you recommend RobCo?') }}
    </v-card-subtitle>
    <v-card-text>
      <v-rating
        v-model="nps.score"
        class="rate-experience__rating"
        :length="5"
        :half-increments="true"
        :hover="true"
        :item-labels="labels"
        color="sunrise-apricot"
        active-color="secondary"
      >
        <template v-slot:item-label="props">
          <span
            :class="`text-${colors[props.index]} text-center`"
            class="font-weight-black text-caption"
            v-html="props.label"
          />
        </template>
      </v-rating>
      <v-textarea
        v-model="nps.comment"
        :label="$gettext('Comments (optional)')"
        :no-resize="true"
        :rows="5"
      />
      <v-icon
        class="position-absolute cursor-pointer"
        style="top: 14px; right: 14px"
        @pointerup="close"
        >mdi-close</v-icon
      >
    </v-card-text>
    <v-card-actions>
      <v-btn color="sunrise-apricot" @pointerup="dontAskAgain">{{
        $gettext("Don't ask again")
      }}</v-btn>
      <v-spacer />
      <v-btn color="primary" @pointerup="submitFeedback">{{
        $gettext('Submit')
      }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<style lang="scss">
.rate-experience {
  position: fixed;
  bottom: 16px;
  right: 16px;
  min-width: 400px;
  z-index: 999999;
  border: 2px solid rgb(var(--v-theme-primary));

  &--mobile {
    width: calc(100% - 32px);
    margin-left: 16px;
    right: unset;
    min-width: unset;
  }

  &__title {
    word-wrap: break-word;
    white-space: pre-wrap;
    margin-right: 24px;
  }

  &__rating {
    width: 100%;
    .v-rating__wrapper {
      flex-grow: 1;
    }
  }
}
</style>
